/**
 * useCurrentSubscription — derives subscription plan from Dexie Cloud license.
 *
 * Reads db.cloud.currentUser (BehaviorSubject) and maps the license object
 * to a SubscriptionPlan with CurrentSubscription. Ported from v1 hooks.ts.
 *
 * Used for feature gating (e.g., calendar events are hidden for free users).
 */

import { useState, useEffect } from "react"
import { db } from "@/db/schema"
import dayjs from "dayjs"
import {
  PaymentTerm,
  PaymentPlan,
  PaymentProvider,
  subscriptionPlans,
  type CurrentSubscription,
  type SubscriptionPlan,
} from "../data/subscriptionPlans"
import { mapStoredSubscription } from "../utils/storedSubscription"

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

export interface SubscriptionInfo {
  plan: SubscriptionPlan
  currentSubscription: CurrentSubscription
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert license.validUntil (string | number | Date) to Unix ms timestamp */
function toUnixMs(expires: string | number | Date): number {
  if (expires instanceof Date) return dayjs(expires).utc().valueOf()
  if (
    typeof expires === "string" &&
    (expires.includes("T") || expires.includes("Z"))
  ) {
    return dayjs(expires).utc().valueOf()
  }
  return typeof expires === "string" ? parseInt(expires, 10) : expires
}

function buildFreeSubscription(
  validUntil: number,
  plan: PaymentPlan,
): CurrentSubscription {
  return {
    term: PaymentTerm.Free,
    plan,
    expires: validUntil.toString(),
    active: false,
    amount: 0,
    currency: "USD",
    interval: "month",
    intervalCount: 1,
    provider: PaymentProvider.None,
    paymentError: false,
  }
}

// ---------------------------------------------------------------------------
// Dev override (DEV only)
// ---------------------------------------------------------------------------

export type DevSubscriptionMode =
  | "real"
  | "paid"
  | "demo"
  | "trial-long"
  | "trial-short"
  | "free"

const DEV_STORAGE_KEY = "dev:subscriptionOverride"

function getDevOverride(): DevSubscriptionMode | null {
  if (!import.meta.env.DEV) return null
  const val = localStorage.getItem(DEV_STORAGE_KEY) as DevSubscriptionMode
  return val && val !== "real" ? val : null
}

export function setDevSubscriptionOverride(mode: DevSubscriptionMode) {
  if (mode === "real") {
    localStorage.removeItem(DEV_STORAGE_KEY)
  } else {
    localStorage.setItem(DEV_STORAGE_KEY, mode)
  }
  // Force re-render by dispatching a storage event
  window.dispatchEvent(new Event("dev:subscription-changed"))
}

function buildDevSubscriptionInfo(
  mode: DevSubscriptionMode,
): SubscriptionInfo | null {
  const findPlan = (id: string) => subscriptionPlans.find((s) => s.id === id)!

  switch (mode) {
    case "paid":
      return {
        plan: findPlan("standard"),
        currentSubscription: {
          term: PaymentTerm.Yearly,
          plan: PaymentPlan.Standard,
          expires: dayjs().add(1, "year").valueOf().toString(),
          active: true,
          amount: 5388,
          currency: "USD",
          interval: "year",
          intervalCount: 1,
          provider: PaymentProvider.Stripe,
          paymentError: false,
        },
      }
    case "demo":
      return {
        plan: findPlan("standard"),
        currentSubscription: {
          term: PaymentTerm.Monthly,
          plan: PaymentPlan.Demo,
          expires: dayjs().add(7, "day").valueOf().toString(),
          active: false,
          amount: 0,
          currency: "USD",
          interval: "month",
          intervalCount: 1,
          provider: PaymentProvider.None,
          paymentError: false,
        },
      }
    case "trial-long":
      return {
        plan: findPlan("standard"),
        currentSubscription: {
          term: PaymentTerm.Free,
          plan: PaymentPlan.Standard,
          expires: dayjs().add(25, "day").valueOf().toString(),
          active: false,
          amount: 0,
          currency: "USD",
          interval: "month",
          intervalCount: 1,
          provider: PaymentProvider.None,
          paymentError: false,
        },
      }
    case "trial-short":
      return {
        plan: findPlan("standard"),
        currentSubscription: {
          term: PaymentTerm.Free,
          plan: PaymentPlan.Standard,
          expires: dayjs().add(3, "day").valueOf().toString(),
          active: false,
          amount: 0,
          currency: "USD",
          interval: "month",
          intervalCount: 1,
          provider: PaymentProvider.None,
          paymentError: false,
        },
      }
    case "free":
      return {
        plan: findPlan("free"),
        currentSubscription: {
          term: PaymentTerm.Free,
          plan: PaymentPlan.Free,
          expires: dayjs().subtract(30, "day").valueOf().toString(),
          active: false,
          amount: 0,
          currency: "USD",
          interval: "month",
          intervalCount: 1,
          provider: PaymentProvider.None,
          paymentError: false,
        },
      }
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Reactively observe the current Dexie Cloud user's license and derive
 * the active subscription plan. Returns null when not logged in or
 * when no license data is available yet.
 *
 * In DEV mode, supports localStorage override via setDevSubscriptionOverride().
 */
export function useCurrentSubscription(): SubscriptionInfo | null {
  const [info, setInfo] = useState<SubscriptionInfo | null>(null)

  // DEV: listen for override changes
  const [devMode, setDevMode] = useState<DevSubscriptionMode | null>(
    getDevOverride,
  )
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const handler = () => setDevMode(getDevOverride())
    window.addEventListener("dev:subscription-changed", handler)
    return () => window.removeEventListener("dev:subscription-changed", handler)
  }, [])

  useEffect(() => {
    // db.cloud.currentUser is a BehaviorSubject-like observable
    const observable = db.cloud.currentUser as unknown as {
      subscribe: (observer: {
        next?: (value: unknown) => void
        error?: (err: unknown) => void
      }) => { unsubscribe: () => void }
    }

    if (!observable?.subscribe) return

    const sub = observable.subscribe({
      next: (currentUser: unknown) => {
        const user = currentUser as {
          license?: {
            type?: string
            status?: string
            validUntil?: string | number | Date
            evalDaysLeft?: number
          }
          data?: {
            subscription?: Record<string, unknown>
          }
        } | null

        if (!user?.license) {
          setInfo(null)
          return
        }

        const { license } = user

        const validUntil = license.validUntil
          ? toUnixMs(license.validUntil)
          : dayjs()
              .add(license.evalDaysLeft ?? 0, "day")
              .utc()
              .valueOf()

        let currentSubscription: CurrentSubscription | null = null

        // Eval license, still valid
        if (
          license.type === "eval" &&
          dayjs(validUntil).isAfter(dayjs()) &&
          license.status !== "expired"
        ) {
          currentSubscription = buildFreeSubscription(
            validUntil,
            PaymentPlan.Standard,
          )
        }
        // Expired eval or prod
        else if (
          ((license.type === "eval" || license.type === "prod") &&
            dayjs(validUntil).isBefore(dayjs())) ||
          license.status === "expired"
        ) {
          currentSubscription = buildFreeSubscription(
            validUntil,
            PaymentPlan.Free,
          )
        }
        // Active production license
        else if (license.type === "prod") {
          currentSubscription = mapStoredSubscription(
            user.data?.subscription,
            validUntil,
          )
        }
        // Demo license
        else if (license.type === "demo") {
          currentSubscription = {
            term: PaymentTerm.Monthly,
            plan: PaymentPlan.Demo,
            expires: dayjs().add(7, "day").toISOString(),
            active: false,
            amount: 0,
            currency: "USD",
            interval: "month",
            intervalCount: 1,
            provider: PaymentProvider.None,
            paymentError: false,
          }
        }

        if (!currentSubscription) {
          setInfo(null)
          return
        }

        const plan = subscriptionPlans.find(
          (s) => s.id === currentSubscription!.plan,
        )
        if (!plan) {
          setInfo(null)
          return
        }

        // Only update state if the subscription actually changed —
        // prevents unnecessary re-renders from Dexie Cloud observable
        // emitting new object references with identical data.
        setInfo((prev) => {
          if (
            prev &&
            prev.plan.id === plan.id &&
            prev.currentSubscription.expires === currentSubscription.expires &&
            prev.currentSubscription.active === currentSubscription.active &&
            prev.currentSubscription.plan === currentSubscription.plan &&
            prev.currentSubscription.paymentError ===
              currentSubscription.paymentError
          ) {
            return prev // same reference → no re-render
          }
          return { plan, currentSubscription }
        })
      },
    })

    return () => sub.unsubscribe()
  }, [])

  // DEV override takes priority
  if (devMode) {
    return buildDevSubscriptionInfo(devMode)
  }

  return info
}
